// Lightweight i18n for the PulseLoop landing page.
// Flat dotted keys; fallback to English when a key is missing.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "nl", label: "Nederlands", short: "NL" },
  { code: "pt", label: "Português", short: "PT" },
  { code: "es", label: "Español", short: "ES" },
  { code: "sv", label: "Svenska", short: "SV" },
  { code: "ro", label: "Română", short: "RO" },
];

const STORAGE_KEY = "pl-lang";

// English (default) — full key catalog
const en = {
  // Nav
  "nav.hypothesis": "INTELLIGENCE BRIEF",
  "nav.intelligence": "MONITORING",
  "nav.methodology": "Methodology",
  "nav.join": "Join Waitlist",
  "nav.lang": "Language",

  // Hero
  "hero.intro": "Introducing PulseLoop",
  "hero.h1.norm": "The market intelligence layer",
  "hero.h1.ital": "you can trust and verify.",
  "hero.sub": "PulseLoop monitors EU markets continuously so every report you walk into a room with is verified, cited, and completely yours to defend.",
  "hero.cta.primary": "Join the Waitlist",
  "hero.cta.access": "Get Access",
  "hero.email.placeholder": "your@email.com",
  "hero.success": "You're on the list — we'll be in touch.",
  "hero.micro": "Early access. No credit card. EU-based.",

  // Hero product card
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Live",
  "hero.card.nav.brief": "Signal Brief",
  "hero.card.nav.history": "History",
  "hero.card.nav.reports": "Reports",
  "hero.card.nav.cert": "Certificate",
  "hero.card.report.title": "French SaaS HR Tech — Q2 2026",
  "hero.card.report.sub": "DACH Market Brief · 847 signals · Generated 14 Apr 2026",
  "hero.card.chip.verified": "● Verified",
  "hero.card.chip.high": "High Confidence",
  "hero.card.chip.geo": "FR · DACH · Nordics",
  "hero.card.chip.sources": "23 Sources",
  "hero.card.sig1.title": "Lucca raises €15M Series B to expand into DACH",
  "hero.card.sig1.body": "Key competitor expanding into your primary market. Funding announced via BFM Business.",
  "hero.card.sig2.title": "New EU AI Act obligations for HR software — March 2026",
  "hero.card.sig2.body": "Regulatory signal affecting all SaaS HR vendors selling in EU. Source: Official EU Journal.",
  "hero.card.sig3.title": "Personio Q1 2026 product update: AI-powered workforce planning",
  "hero.card.sig3.body": "Feature parity risk. Signal from Personio Engineering Blog.",

  // Trust strip
  "trust.label": "INTELLIGENCE COVERING",
  "trust.f1": "France",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Nordics",
  "trust.f5": "Southern EU",
  "trust.f6": "CEE",
  "trust.f7": "Baltics",
  "trust.f8": "Other EU",

  // Feature 1 — Hypothesis
  "f1.tag": "The Intelligence Brief",
  "f1.h": "Intelligence that starts with your brief, not our assumptions.",
  "f1.body": "Paste your client brief or answer a few questions. PulseLoop structures a research plan in plain language and confirms it with you before gathering a single signal, so what comes back is exactly what your client needs.",
  "f1.cta": "Join the waitlist →",
  "f1.card.title": "Research Hypothesis",
  "f1.card.live": "Live",
  "f1.card.market.k": "Market",
  "f1.card.market.v": "French pharmacy skincare — indie & challenger brands",
  "f1.card.question.k": "Question",
  "f1.card.question.v": "How are indie brands gaining shelf space and building social presence?",
  "f1.card.tracking.k": "Tracking",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geography",
  "f1.card.geo.v": "France · French-speaking Belgium",
  "f1.card.confirm": "Confirm hypothesis  →",

  // Feature 2 — Intelligence
  "f2.tag": "THE MONITORING",
  "f2.h": "Signals from 100+ verified EU sources. Continuously monitored.",
  "f2.body": "PulseLoop covers the publications, regulators, funding bodies, and communities that shape EU markets across FR, DACH, Nordics, and beyond. Intelligence that accumulates in your workspace while you work, so you're always briefed before your client asks.",
  "f2.cta": "Join the waitlist →",
  "f2.feed.title": "Live Signal Feed",
  "f2.feed.new": "3 new",
  "f2.feed.t1": "2 hours ago",
  "f2.feed.t2": "6 hours ago",
  "f2.feed.t3": "1 day ago",
  "f2.feed.s1.text": "Typology lance une gamme professionnelle en exclusivité pharmacie...",
  "f2.feed.s1.chip": "Product Launch",
  "f2.feed.s2.text": "New cosmetic ingredient regulation enters enforcement phase across member states...",
  "f2.feed.s2.chip": "Regulatory",
  "f2.feed.s3.text": "Novexpert lève 4M€ en Serie A pour accélérer son déploiement retail...",
  "f2.feed.s3.chip": "Funding",

  // Feature 3 — Certificate
  "f3.tag": "THE METHODOLOGY",
  "f3.h": "Know exactly where your intelligence comes from.",
  "f3.body": "Every report ships with a Methodology Certificate documenting sources used, signals collected, confidence thresholds, and deduplication method. Full transparency on every claim, so you can present with complete confidence and defend every insight in the room.",
  "f3.cta": "Join the waitlist →",
  "f3.cert.tag": "Methodology Certificate",
  "f3.cert.name1": "French Skincare Indie",
  "f3.cert.name2": "Market Brief — Q1 2026",
  "f3.cert.badge": "● Verified",
  "f3.cert.l1.k": "Sources",
  "f3.cert.l1.v": "23 distinct sources",
  "f3.cert.l2.k": "Date range",
  "f3.cert.l2.v": "01 Mar – 31 Mar 2026",
  "f3.cert.l3.k": "Total signals",
  "f3.cert.l3.v": "847 retrieved",
  "f3.cert.l4.k": "Deduplication",
  "f3.cert.l4.v": "Cosine similarity > 0.92",
  "f3.cert.l5.k": "High confidence",
  "f3.cert.l5.v": "10+ signals, 5+ sources",
  "f3.cert.l6.k": "Low confidence",
  "f3.cert.l6.v": "<3 signals — flagged",
  "f3.cert.l7.k": "Language",
  "f3.cert.l7.v": "French",
  "f3.cert.foot": "Generated by PulseLoop · pulseloop.io",

  // Audience
  "aud.h": "For the consultant who delivers intelligence clients can act on.",
  "aud.sub": "Whether you work alone or run agency, PulseLoop gives you verified intelligence infrastructure so every report you deliver is one your clients can act on with confidence.",
  "aud.solo.pill": "Most Popular",
  "aud.solo.name": "The Solo Consultant",
  "aud.solo.tag": "You are your own research team. You need to sound like you have five.",
  "aud.solo.i1": "Cited reports your clients trust",
  "aud.solo.i2": "On-demand answers from intelligence you already own.",
  "aud.solo.i3": "Intelligence that compounds with every client",
  "aud.agency.pill": "Agencies Welcome",
  "aud.agency.name": "The Boutique Agency",
  "aud.agency.tag": "You manage 6 client accounts. Each one deserves its own market intelligence layer.",
  "aud.agency.i1": "Separate intelligence workspace per client",
  "aud.agency.i2": "White-label report sharing",
  "aud.agency.i3": "One subscription, multiple clients covered",
  "aud.join": "Join waitlist →",

  // Final CTA
  "final.cl1": "A briefing room",
  "final.cl2": "that never sleeps.",
  "final.h": "Be among the first consultants in.",
  "final.cta": "✛  Get Early Access",
  "final.success.h": "You're on the list.",
  "final.success.s": "Early access · We'll be in touch",
  "final.micro": "Early access · EU-based · Built for the way you work",

  // Footer
  "foot.tagline": "Verified EU market intelligence for growth consultants and boutique agencies.",
  "foot.product": "Product",
  "foot.company": "Company",
  "foot.markets": "Markets",
  "foot.about": "About",
  "foot.contact": "Contact",
  "foot.privacy": "Privacy",
  "foot.france": "France",
  "foot.dach": "DACH",
  "foot.nordics": "Nordics",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · EU-hosted · GDPR compliant",
  "foot.l.privacy": "Privacy",
  "foot.l.terms": "Terms",
  "foot.l.status": "Status",

  // Toasts
  "toast.dup.title": "Already on the list",
  "toast.dup.body": "Your email has already been submitted, we will get back to you soon.",
  "toast.ok.title": "Thank you for joining the waiting list!",
  "toast.ok.body": "We will get back to you soon.",
  "toast.err.title": "Could not submit",
  "toast.err.body": "Something went wrong. Please try again.",
};

const fr = {
  "nav.hypothesis": "NOTE DE SYNTHÈSE",
  "nav.intelligence": "SURVEILLANCE",
  "nav.methodology": "Méthodologie",
  "nav.join": "Rejoindre",
  "nav.lang": "Langue",

  "hero.intro": "Découvrez PulseLoop",
  "hero.h1.norm": "La couche d'intelligence de marché",
  "hero.h1.ital": "que vous pouvez faire confiance et vérifier.",
  "hero.sub": "PulseLoop surveille les marchés de l'UE en continu, de sorte que chaque rapport avec lequel vous entrez dans une salle est vérifié, cité et totalement vôtre à défendre.",
  "hero.cta.primary": "Rejoindre la liste d'attente",
  "hero.cta.access": "Obtenir l'accès",
  "hero.email.placeholder": "votre@email.com",
  "hero.success": "Vous êtes sur la liste — nous vous contacterons.",
  "hero.micro": "Accès anticipé. Sans carte bancaire. Basé en UE.",

  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "En direct",
  "hero.card.nav.brief": "Note de signaux",
  "hero.card.nav.history": "Historique",
  "hero.card.nav.reports": "Rapports",
  "hero.card.nav.cert": "Certificat",
  "hero.card.report.title": "SaaS RH français — T2 2026",
  "hero.card.report.sub": "Note marché DACH · 847 signaux · Généré le 14 avr. 2026",
  "hero.card.chip.verified": "● Vérifié",
  "hero.card.chip.high": "Confiance élevée",
  "hero.card.chip.geo": "FR · DACH · Nordiques",
  "hero.card.chip.sources": "23 sources",
  "hero.card.sig1.title": "Lucca lève 15 M€ en Série B pour s'étendre en DACH",
  "hero.card.sig1.body": "Concurrent clé s'étendant sur votre marché principal. Annonce via BFM Business.",
  "hero.card.sig2.title": "Nouvelles obligations de l'AI Act européen pour les logiciels RH — mars 2026",
  "hero.card.sig2.body": "Signal réglementaire concernant tous les éditeurs SaaS RH vendant en UE. Source : Journal officiel UE.",
  "hero.card.sig3.title": "Personio T1 2026 : planification RH propulsée par IA",
  "hero.card.sig3.body": "Risque de parité fonctionnelle. Source : Blog Ingénierie Personio.",

  "trust.label": "COUVERTURE INTELLIGENCE",
  "trust.f1": "France",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Nordiques",
  "trust.f5": "Europe du Sud",
  "trust.f6": "PECO",
  "trust.f7": "Pays Baltes",
  "trust.f8": "Autre UE",

  "f1.tag": "Le briefing d'intelligence",
  "f1.h": "Une intelligence qui commence par votre brief, pas par nos suppositions.",
  "f1.body": "Collez votre brief client ou répondez à quelques questions. PulseLoop structure un plan de recherche en langage clair et le confirme avec vous avant de recueillir un seul signal, de sorte que ce qui revient est exactement ce dont votre client a besoin.",
  "f1.cta": "Rejoindre la liste →",
  "f1.card.title": "Hypothèse de recherche",
  "f1.card.live": "En direct",
  "f1.card.market.k": "Marché",
  "f1.card.market.v": "Skincare en pharmacie française — marques indé & challengers",
  "f1.card.question.k": "Question",
  "f1.card.question.v": "Comment les marques indé gagnent-elles du linéaire et présence sociale ?",
  "f1.card.tracking.k": "Suivi",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Géographie",
  "f1.card.geo.v": "France · Belgique francophone",
  "f1.card.confirm": "Valider l'hypothèse  →",

  "f2.tag": "LA SURVEILLANCE",
  "f2.h": "Signaux de 100+ sources européennes vérifiées. Surveillé en continu.",
  "f2.body": "PulseLoop couvre les publications, les régulateurs, les organismes de financement et les communautés qui façonnent les marchés de l'UE en France, DACH, Nordiques et au-delà. Une intelligence qui s'accumule dans votre espace de travail pendant que vous travaillez, pour que vous soyez toujours briefé avant que votre client ne le demande.",
  "f2.cta": "Rejoindre la liste →",
  "f2.feed.title": "Flux de signaux en direct",
  "f2.feed.new": "3 nouveaux",
  "f2.feed.t1": "il y a 2 heures",
  "f2.feed.t2": "il y a 6 heures",
  "f2.feed.t3": "il y a 1 jour",
  "f2.feed.s1.text": "Typology lance une gamme professionnelle en exclusivité pharmacie...",
  "f2.feed.s1.chip": "Lancement produit",
  "f2.feed.s2.text": "Une nouvelle réglementation cosmétique entre en application dans l'UE...",
  "f2.feed.s2.chip": "Réglementation",
  "f2.feed.s3.text": "Novexpert lève 4M€ en Série A pour accélérer son déploiement retail...",
  "f2.feed.s3.chip": "Levée de fonds",

  "f3.tag": "LA MÉTHODOLOGIE",
  "f3.h": "Sachez exactement d'où vient votre intelligence.",
  "f3.body": "Chaque rapport est livré avec un Certificat de Méthodologie documentant les sources utilisées, les signaux collectés, les seuils de confiance et la méthode de déduplication. Une transparence totale sur chaque affirmation, pour que vous puissiez présenter en toute confiance et défendre chaque insight dans la salle.",
  "f3.cta": "Rejoindre la liste →",
  "f3.cert.tag": "Certificat de méthodologie",
  "f3.cert.name1": "Skincare français indé",
  "f3.cert.name2": "Note marché — T1 2026",
  "f3.cert.badge": "● Vérifié",
  "f3.cert.l1.k": "Sources",
  "f3.cert.l1.v": "23 sources distinctes",
  "f3.cert.l2.k": "Plage de dates",
  "f3.cert.l2.v": "01 mars – 31 mars 2026",
  "f3.cert.l3.k": "Signaux totaux",
  "f3.cert.l3.v": "847 récupérés",
  "f3.cert.l4.k": "Déduplication",
  "f3.cert.l4.v": "Similarité cosinus > 0,92",
  "f3.cert.l5.k": "Confiance élevée",
  "f3.cert.l5.v": "10+ signaux, 5+ sources",
  "f3.cert.l6.k": "Confiance faible",
  "f3.cert.l6.v": "<3 signaux — signalé",
  "f3.cert.l7.k": "Langue",
  "f3.cert.l7.v": "Français",
  "f3.cert.foot": "Généré par PulseLoop · pulseloop.io",

  "aud.h": "Pour le consultant qui livre une intelligence sur laquelle les clients peuvent agir.",
  "aud.sub": "Que vous travailliez seul ou que vous dirigiez une agence, PulseLoop vous offre une infrastructure d'intelligence vérifiée afin que chaque rapport que vous livrez soit un rapport sur lequel vos clients peuvent agir en toute confiance.",
  "aud.solo.pill": "Le plus populaire",
  "aud.solo.name": "Le consultant solo",
  "aud.solo.tag": "Vous êtes votre propre équipe de recherche. Vous devez paraître comme cinq.",
  "aud.solo.i1": "Des rapports sourcés en qui vos clients ont confiance",
  "aud.solo.i2": "Réponses à la demande à partir de l'intelligence que vous possédez déjà.",
  "aud.solo.i3": "Une intelligence qui se cumule avec chaque client",
  "aud.agency.pill": "Agences bienvenues",
  "aud.agency.name": "L'agence boutique",
  "aud.agency.tag": "Vous gérez 6 comptes clients. Chacun mérite sa propre couche d'intelligence de marché.",
  "aud.agency.i1": "Espace d'intelligence séparé par client",
  "aud.agency.i2": "Partage de rapports en marque blanche",
  "aud.agency.i3": "Un abonnement, plusieurs clients couverts",
  "aud.join": "Rejoindre →",

  "final.cl1": "Une salle de briefing",
  "final.cl2": "qui ne dort jamais.",
  "final.h": "Soyez parmi les premiers consultants admis.",
  "final.cta": "✛  Obtenir l'accès anticipé",
  "final.success.h": "Vous êtes sur la liste.",
  "final.success.s": "Accès anticipé · Nous reviendrons vers vous",
  "final.micro": "Accès anticipé · Basé en UE · Conçu pour votre manière de travailler",

  "foot.tagline": "Intelligence de marché européenne vérifiée pour consultants en croissance et agences boutiques.",
  "foot.product": "Produit",
  "foot.company": "Société",
  "foot.markets": "Marchés",
  "foot.about": "À propos",
  "foot.contact": "Contact",
  "foot.privacy": "Confidentialité",
  "foot.france": "France",
  "foot.dach": "DACH",
  "foot.nordics": "Pays nordiques",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · Hébergé en UE · Conforme RGPD",
  "foot.l.privacy": "Confidentialité",
  "foot.l.terms": "Conditions",
  "foot.l.status": "État",

  "toast.dup.title": "Déjà inscrit",
  "toast.dup.body": "Votre email a déjà été soumis, nous reviendrons vers vous bientôt.",
  "toast.ok.title": "Merci d'avoir rejoint la liste d'attente !",
  "toast.ok.body": "Nous reviendrons vers vous bientôt.",
  "toast.err.title": "Soumission impossible",
  "toast.err.body": "Une erreur est survenue. Veuillez réessayer.",
};

const de = {
  "nav.hypothesis": "INTELLIGENCE BRIEF",
  "nav.intelligence": "MONITORING",
  "nav.methodology": "Methodik",
  "nav.join": "Anmelden",
  "nav.lang": "Sprache",
  "hero.intro": "Wir präsentieren PulseLoop",
  "hero.h1.norm": "Die Marktintelligenz-Ebene,",
  "hero.h1.ital": "der Sie vertrauen und verifizieren können.",
  "hero.sub": "PulseLoop überwacht die EU-Märkte kontinuierlich, sodass jeder Bericht, mit dem Sie einen Raum betreten, verifiziert, zitiert und vollständig von Ihnen zu verteidigen ist.",
  "hero.cta.primary": "Auf die Warteliste",
  "hero.cta.access": "Zugang erhalten",
  "hero.email.placeholder": "ihre@email.com",
  "hero.success": "Sie sind auf der Liste — wir melden uns.",
  "hero.micro": "Frühzugang. Keine Kreditkarte. EU-basiert.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Live",
  "hero.card.nav.brief": "Signal-Bericht",
  "hero.card.nav.history": "Verlauf",
  "hero.card.nav.reports": "Berichte",
  "hero.card.nav.cert": "Zertifikat",
  "hero.card.report.title": "Französische SaaS-HR-Tech — Q2 2026",
  "hero.card.report.sub": "DACH-Marktbericht · 847 Signale · Erstellt am 14. Apr 2026",
  "hero.card.chip.verified": "● Verifiziert",
  "hero.card.chip.high": "Hohe Konfidenz",
  "hero.card.chip.geo": "FR · DACH · Nordics",
  "hero.card.chip.sources": "23 Quellen",
  "hero.card.sig1.title": "Lucca sammelt 15 Mio. € in Serie B für DACH-Expansion",
  "hero.card.sig1.body": "Wichtiger Wettbewerber expandiert in Ihren Hauptmarkt. Finanzierung gemeldet via BFM Business.",
  "hero.card.sig2.title": "Neue EU-AI-Act-Pflichten für HR-Software — März 2026",
  "hero.card.sig2.body": "Regulatorisches Signal für alle SaaS-HR-Anbieter in der EU. Quelle: Amtsblatt der EU.",
  "hero.card.sig3.title": "Personio Q1 2026: KI-gestützte Personalplanung",
  "hero.card.sig3.body": "Risiko der Funktionsparität. Quelle: Personio Engineering Blog.",
  "trust.label": "INTELLIGENCE-ABDECKUNG",
  "trust.f1": "Frankreich",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Nordics",
  "trust.f5": "Südeuropa",
  "trust.f6": "Mittel- und Osteuropa",
  "trust.f7": "Baltikum",
  "trust.f8": "Übrige EU",
  "f1.tag": "Das Intelligence-Briefing",
  "f1.h": "Intelligenz, die mit Ihrem Briefing beginnt, nicht mit unseren Annahmen.",
  "f1.body": "Fügen Sie Ihr Kundenbriefing ein oder beantworten Sie ein paar Fragen. PulseLoop strukturiert einen Forschungsplan in verständlicher Sprache und bestätigt diesen mit Ihnen, bevor ein einziges Signal erfasst wird, sodass das Ergebnis genau das ist, was Ihr Kunde benötigt.",
  "f1.cta": "Auf die Warteliste →",
  "f1.card.title": "Recherche-Hypothese",
  "f1.card.live": "Live",
  "f1.card.market.k": "Markt",
  "f1.card.market.v": "Französische Apotheken-Skincare — Indie- & Challenger-Marken",
  "f1.card.question.k": "Frage",
  "f1.card.question.v": "Wie gewinnen Indie-Marken Regalplatz und Social-Präsenz?",
  "f1.card.tracking.k": "Tracking",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografie",
  "f1.card.geo.v": "Frankreich · französischsprachiges Belgien",
  "f1.card.confirm": "Hypothese bestätigen  →",
  "f2.tag": "DIE ÜBERWACHUNG",
  "f2.h": "Signale aus 100+ verifizierten EU-Quellen. Kontinuierlich überwacht.",
  "f2.body": "PulseLoop deckt die Publikationen, Regulierungsbehörden, Förderinstanzen und Communities ab, die die EU-Märkte in FR, DACH, den nordischen Ländern und darüber hinaus prägen. Intelligenz, die sich in Ihrem Workspace ansammelt, während Sie arbeiten, sodass Sie immer informiert sind, bevor Ihr Kunde fragt.",
  "f2.cta": "Auf die Warteliste →",
  "f2.feed.title": "Live-Signal-Feed",
  "f2.feed.new": "3 neu",
  "f2.feed.t1": "vor 2 Stunden",
  "f2.feed.t2": "vor 6 Stunden",
  "f2.feed.t3": "vor 1 Tag",
  "f2.feed.s1.text": "Typology launcht professionelle Reihe exklusiv für Apotheken...",
  "f2.feed.s1.chip": "Produkt-Launch",
  "f2.feed.s2.text": "Neue Verordnung zu Kosmetik-Inhaltsstoffen tritt EU-weit in Kraft...",
  "f2.feed.s2.chip": "Regulatorisch",
  "f2.feed.s3.text": "Novexpert sammelt 4 Mio. € in Serie A für Retail-Ausbau...",
  "f2.feed.s3.chip": "Finanzierung",
  "f3.tag": "DIE METHODIK",
  "f3.h": "Wissen Sie genau, woher Ihre Informationen stammen.",
  "f3.body": "Jeder Bericht enthält ein Methodik-Zertifikat, das die verwendeten Quellen, die gesammelten Signale, die Konfidenzschwellen und die Deduplizierungsmethode dokumentiert. Volle Transparenz bei jeder Aussage, sodass Sie mit vollem Vertrauen präsentieren und jede Erkenntnis im Raum verteidigen können.",
  "f3.cta": "Auf die Warteliste →",
  "f3.cert.tag": "Methodik-Zertifikat",
  "f3.cert.name1": "Französisches Indie-Skincare",
  "f3.cert.name2": "Marktbericht — Q1 2026",
  "f3.cert.badge": "● Verifiziert",
  "f3.cert.l1.k": "Quellen",
  "f3.cert.l1.v": "23 verschiedene Quellen",
  "f3.cert.l2.k": "Zeitraum",
  "f3.cert.l2.v": "01. März – 31. März 2026",
  "f3.cert.l3.k": "Signale gesamt",
  "f3.cert.l3.v": "847 erfasst",
  "f3.cert.l4.k": "Deduplizierung",
  "f3.cert.l4.v": "Kosinus-Ähnlichkeit > 0,92",
  "f3.cert.l5.k": "Hohe Konfidenz",
  "f3.cert.l5.v": "10+ Signale, 5+ Quellen",
  "f3.cert.l6.k": "Niedrige Konfidenz",
  "f3.cert.l6.v": "<3 Signale — markiert",
  "f3.cert.l7.k": "Sprache",
  "f3.cert.l7.v": "Französisch",
  "f3.cert.foot": "Erstellt von PulseLoop · pulseloop.io",
  "aud.h": "Für den Berater, der Informationen liefert, auf die Kunden reagieren können.",
  "aud.sub": "Ob Sie alleine arbeiten oder eine Agentur leiten – PulseLoop bietet Ihnen eine verifizierte Intelligence-Infrastruktur, sodass jeder von Ihnen gelieferte Bericht einer ist, auf den Ihre Kunden mit Vertrauen reagieren können.",
  "aud.solo.pill": "Am beliebtesten",
  "aud.solo.name": "Der Solo-Berater",
  "aud.solo.tag": "Sie sind Ihr eigenes Recherche-Team. Sie sollen klingen, als wären Sie zu fünft.",
  "aud.solo.i1": "Quellengeprüfte Berichte, denen Ihre Kunden vertrauen",
  "aud.solo.i2": "Antworten auf Abruf aus der Intelligence, die Sie bereits besitzen.",
  "aud.solo.i3": "Intelligenz, die sich mit jedem Kunden summiert",
  "aud.agency.pill": "Agenturen willkommen",
  "aud.agency.name": "Die Boutique-Agentur",
  "aud.agency.tag": "Sie betreuen 6 Kundenkonten. Jedes verdient seine eigene Marktintelligenz-Ebene.",
  "aud.agency.i1": "Eigener Intelligenz-Workspace pro Kunde",
  "aud.agency.i2": "White-Label-Berichte teilen",
  "aud.agency.i3": "Ein Abo, mehrere Kunden abgedeckt",
  "aud.join": "Anmelden →",
  "final.cl1": "Ein Briefing-Raum",
  "final.cl2": "der nie schläft.",
  "final.h": "Seien Sie unter den ersten Beratern dabei.",
  "final.cta": "✛  Frühzugang sichern",
  "final.success.h": "Sie sind auf der Liste.",
  "final.success.s": "Frühzugang · Wir melden uns",
  "final.micro": "Frühzugang · EU-basiert · Gemacht für Ihre Arbeitsweise",
  "foot.tagline": "Geprüfte EU-Marktinformationen für Wachstumsberater und Boutique-Agenturen.",
  "foot.product": "Produkt",
  "foot.company": "Unternehmen",
  "foot.markets": "Märkte",
  "foot.about": "Über uns",
  "foot.contact": "Kontakt",
  "foot.privacy": "Datenschutz",
  "foot.france": "Frankreich",
  "foot.dach": "DACH",
  "foot.nordics": "Nordics",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · EU-gehostet · DSGVO-konform",
  "foot.l.privacy": "Datenschutz",
  "foot.l.terms": "AGB",
  "foot.l.status": "Status",
  "toast.dup.title": "Schon angemeldet",
  "toast.dup.body": "Ihre E-Mail wurde bereits eingereicht, wir melden uns bald.",
  "toast.ok.title": "Danke für die Anmeldung!",
  "toast.ok.body": "Wir melden uns bald.",
  "toast.err.title": "Übermittlung fehlgeschlagen",
  "toast.err.body": "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
};

const nl = {
  "nav.hypothesis": "INTELLIGENCE BRIEF",
  "nav.intelligence": "MONITORING",
  "nav.methodology": "Methodologie",
  "nav.join": "Aanmelden",
  "nav.lang": "Taal",
  "hero.intro": "Maak kennis met PulseLoop",
  "hero.h1.norm": "De marktintelligentielaag",
  "hero.h1.ital": "die u kunt vertrouwen en verifiëren.",
  "hero.sub": "PulseLoop monitort de EU-markten continu, zodat elk rapport waarmee u een kamer binnenstapt, geverifieerd en geciteerd is, en volledig van u is om te verdedigen.",
  "hero.cta.primary": "Op de wachtlijst",
  "hero.cta.access": "Toegang krijgen",
  "hero.email.placeholder": "uw@email.com",
  "hero.success": "U staat op de lijst — we nemen contact op.",
  "hero.micro": "Vroege toegang. Geen creditcard. EU-gevestigd.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Live",
  "hero.card.nav.brief": "Signaal-briefing",
  "hero.card.nav.history": "Geschiedenis",
  "hero.card.nav.reports": "Rapporten",
  "hero.card.nav.cert": "Certificaat",
  "hero.card.report.title": "Franse SaaS HR Tech — Q2 2026",
  "hero.card.report.sub": "DACH Marktbriefing · 847 signalen · Gegenereerd 14 apr 2026",
  "hero.card.chip.verified": "● Geverifieerd",
  "hero.card.chip.high": "Hoge zekerheid",
  "hero.card.chip.geo": "FR · DACH · Nordics",
  "hero.card.chip.sources": "23 bronnen",
  "hero.card.sig1.title": "Lucca haalt €15M Series B op om uit te breiden naar DACH",
  "hero.card.sig1.body": "Belangrijke concurrent breidt uit naar uw hoofdmarkt. Aankondiging via BFM Business.",
  "hero.card.sig2.title": "Nieuwe EU-AI-Act-verplichtingen voor HR-software — maart 2026",
  "hero.card.sig2.body": "Regulatoir signaal voor alle SaaS HR-leveranciers in de EU. Bron: Officieel EU-blad.",
  "hero.card.sig3.title": "Personio Q1 2026: AI-gedreven personeelsplanning",
  "hero.card.sig3.body": "Risico van functiepariteit. Bron: Personio Engineering Blog.",
  "trust.label": "INTELLIGENTIE DEKKING",
  "trust.f1": "Frankrijk",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Nordics",
  "trust.f5": "Zuid-Europa",
  "trust.f6": "CEE",
  "trust.f7": "Baltische staten",
  "trust.f8": "Overige EU",
  "f1.tag": "Het Intelligence Briefing",
  "f1.h": "Intelligentie die begint bij uw briefing, niet bij onze aannames.",
  "f1.body": "Plak uw klantbriefing of beantwoord een paar vragen. PulseLoop structureert een onderzoeksplan in duidelijke taal en bevestigt dit met u voordat er één signaal wordt verzameld, zodat wat terugkomt precies is wat uw klant nodig heeft.",
  "f1.cta": "Op de wachtlijst →",
  "f1.card.title": "Onderzoekshypothese",
  "f1.card.live": "Live",
  "f1.card.market.k": "Markt",
  "f1.card.market.v": "Franse apotheek-skincare — indie & uitdager-merken",
  "f1.card.question.k": "Vraag",
  "f1.card.question.v": "Hoe winnen indie-merken schapruimte en sociale aanwezigheid?",
  "f1.card.tracking.k": "Tracking",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografie",
  "f1.card.geo.v": "Frankrijk · Franstalig België",
  "f1.card.confirm": "Hypothese bevestigen  →",
  "f2.tag": "DE MONITORING",
  "f2.h": "Signalen uit 100+ geverifieerde EU-bronnen. Continu gemonitord.",
  "f2.body": "PulseLoop bestrijkt de publicaties, toezichthouders, financieringsinstanties en gemeenschappen die de EU-markten in FR, DACH, de Nordics en daarbuiten vormen. Intelligentie die zich in uw werkruimte verzamelt terwijl u werkt, zodat u altijd gebriefd bent voordat uw klant ernaar vraagt.",
  "f2.cta": "Op de wachtlijst →",
  "f2.feed.title": "Live signaalstroom",
  "f2.feed.new": "3 nieuw",
  "f2.feed.t1": "2 uur geleden",
  "f2.feed.t2": "6 uur geleden",
  "f2.feed.t3": "1 dag geleden",
  "f2.feed.s1.text": "Typology lanceert professionele lijn exclusief in apotheek...",
  "f2.feed.s1.chip": "Productlancering",
  "f2.feed.s2.text": "Nieuwe cosmetica-ingrediëntenregelgeving treedt in werking in lidstaten...",
  "f2.feed.s2.chip": "Regelgeving",
  "f2.feed.s3.text": "Novexpert haalt 4M€ op in Series A voor retailuitbreiding...",
  "f2.feed.s3.chip": "Financiering",
  "f3.tag": "DE METHODOLOGIE",
  "f3.h": "Weet precies waar uw intelligentie vandaan komt.",
  "f3.body": "Elk rapport wordt geleverd met een Methodologiecertificaat waarin de gebruikte bronnen, verzamelde signalen, betrouwbaarheidsdrempels en ontdubbelingsmethode zijn gedocumenteerd. Volledige transparantie bij elke claim, zodat u met volledig vertrouwen kunt presenteren en elk inzicht in de kamer kunt verdedigen.",
  "f3.cta": "Op de wachtlijst →",
  "f3.cert.tag": "Methodologie-certificaat",
  "f3.cert.name1": "Frans indie-skincare",
  "f3.cert.name2": "Marktbriefing — Q1 2026",
  "f3.cert.badge": "● Geverifieerd",
  "f3.cert.l1.k": "Bronnen",
  "f3.cert.l1.v": "23 unieke bronnen",
  "f3.cert.l2.k": "Periode",
  "f3.cert.l2.v": "01 mrt – 31 mrt 2026",
  "f3.cert.l3.k": "Totaal signalen",
  "f3.cert.l3.v": "847 opgehaald",
  "f3.cert.l4.k": "Deduplicatie",
  "f3.cert.l4.v": "Cosinus-gelijkenis > 0,92",
  "f3.cert.l5.k": "Hoge zekerheid",
  "f3.cert.l5.v": "10+ signalen, 5+ bronnen",
  "f3.cert.l6.k": "Lage zekerheid",
  "f3.cert.l6.v": "<3 signalen — gemarkeerd",
  "f3.cert.l7.k": "Taal",
  "f3.cert.l7.v": "Frans",
  "f3.cert.foot": "Gegenereerd door PulseLoop · pulseloop.io",
  "aud.h": "Voor de consultant die intelligentie levert waar klanten op kunnen handelen.",
  "aud.sub": "Of u nu alleen werkt of een bureau leidt, PulseLoop biedt u een geverifieerde intelligentie-infrastructuur, zodat elk rapport dat u aflevert er een is waar uw klanten met vertrouwen op kunnen handelen.",
  "aud.solo.pill": "Populairst",
  "aud.solo.name": "De solo-consultant",
  "aud.solo.tag": "U bent uw eigen onderzoeksteam. U moet klinken alsof u er vijf bent.",
  "aud.solo.i1": "Bronvermelde rapporten waar klanten op vertrouwen",
  "aud.solo.i2": "On-demand antwoorden van de intelligence die u al bezit.",
  "aud.solo.i3": "Intelligentie die met elke klant groeit",
  "aud.agency.pill": "Bureaus welkom",
  "aud.agency.name": "Het boutique-bureau",
  "aud.agency.tag": "U beheert 6 klantaccounts. Elke verdient een eigen marktintelligentielaag.",
  "aud.agency.i1": "Aparte intelligentie-werkruimte per klant",
  "aud.agency.i2": "White-label rapporten delen",
  "aud.agency.i3": "Eén abonnement, meerdere klanten gedekt",
  "aud.join": "Aanmelden →",
  "final.cl1": "Een briefingroom",
  "final.cl2": "die nooit slaapt.",
  "final.h": "Wees onder de eerste consultants die toegang krijgen.",
  "final.cta": "✛  Vroege toegang",
  "final.success.h": "U staat op de lijst.",
  "final.success.s": "Vroege toegang · We nemen contact op",
  "final.micro": "Vroege toegang · EU-gevestigd · Gemaakt voor uw werkwijze",
  "foot.tagline": "Geverifieerde EU-marktinformatie voor groei-consultants en boutique-bureaus.",
  "foot.product": "Product",
  "foot.company": "Bedrijf",
  "foot.markets": "Markten",
  "foot.about": "Over ons",
  "foot.contact": "Contact",
  "foot.privacy": "Privacy",
  "foot.france": "Frankrijk",
  "foot.dach": "DACH",
  "foot.nordics": "Nordics",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · EU-gehost · AVG-conform",
  "foot.l.privacy": "Privacy",
  "foot.l.terms": "Voorwaarden",
  "foot.l.status": "Status",
  "toast.dup.title": "Al op de lijst",
  "toast.dup.body": "Uw email is al ingediend, we nemen binnenkort contact op.",
  "toast.ok.title": "Bedankt voor uw aanmelding!",
  "toast.ok.body": "We nemen binnenkort contact op.",
  "toast.err.title": "Indienen mislukt",
  "toast.err.body": "Er ging iets mis. Probeer opnieuw.",
};

const pt = {
  "nav.hypothesis": "RELATÓRIO DE INTELIGÊNCIA",
  "nav.intelligence": "MONITORAMENTO",
  "nav.methodology": "Metodologia",
  "nav.join": "Inscrever-me",
  "nav.lang": "Idioma",
  "hero.intro": "Apresentamos PulseLoop",
  "hero.h1.norm": "A camada de inteligência de mercado",
  "hero.h1.ital": "em que pode confiar e verificar.",
  "hero.sub": "A PulseLoop monitoriza os mercados da UE continuamente, para que cada relatório com que entre numa sala seja verificado, citado e totalmente seu para defender.",
  "hero.cta.primary": "Entrar na lista",
  "hero.cta.access": "Obter acesso",
  "hero.email.placeholder": "seu@email.com",
  "hero.success": "Você está na lista — entraremos em contato.",
  "hero.micro": "Acesso antecipado. Sem cartão. Sediado na UE.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Ao vivo",
  "hero.card.nav.brief": "Resumo de Sinais",
  "hero.card.nav.history": "Histórico",
  "hero.card.nav.reports": "Relatórios",
  "hero.card.nav.cert": "Certificado",
  "hero.card.report.title": "SaaS HR francês — T2 2026",
  "hero.card.report.sub": "Briefing DACH · 847 sinais · Gerado em 14 abr 2026",
  "hero.card.chip.verified": "● Verificado",
  "hero.card.chip.high": "Alta confiança",
  "hero.card.chip.geo": "FR · DACH · Nórdicos",
  "hero.card.chip.sources": "23 fontes",
  "hero.card.sig1.title": "Lucca capta €15M em Série B para expandir para a DACH",
  "hero.card.sig1.body": "Concorrente-chave a expandir para o seu mercado principal. Anúncio via BFM Business.",
  "hero.card.sig2.title": "Novas obrigações do AI Act da UE para software de RH — março 2026",
  "hero.card.sig2.body": "Sinal regulatório que afeta todos os fornecedores SaaS HR na UE. Fonte: Jornal Oficial UE.",
  "hero.card.sig3.title": "Personio T1 2026: planeamento RH com IA",
  "hero.card.sig3.body": "Risco de paridade de funcionalidades. Fonte: Personio Engineering Blog.",
  "trust.label": "COBERTURA DE INTELIGÊNCIA",
  "trust.f1": "França",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Países Nórdicos",
  "trust.f5": "Europa do Sul",
  "trust.f6": "CEE",
  "trust.f7": "Países Bálticos",
  "trust.f8": "Outra UE",
  "f1.tag": "O Briefing de Inteligência",
  "f1.h": "Inteligência que começa com o seu briefing, não com as nossas suposições.",
  "f1.body": "Cole o briefing do seu cliente ou responda a algumas perguntas. O PulseLoop estrutura um plan de pesquisa em linguagem clara e confirma-o consigo antes de recolher um único sinal, para que o que receba seja exatamente o que o seu cliente precisa.",
  "f1.cta": "Entrar na lista →",
  "f1.card.title": "Hipótese de pesquisa",
  "f1.card.live": "Ao vivo",
  "f1.card.market.k": "Mercado",
  "f1.card.market.v": "Skincare farmacêutico francês — marcas indie & desafiantes",
  "f1.card.question.k": "Pergunta",
  "f1.card.question.v": "Como as marcas indie ganham espaço de prateleira e presença social?",
  "f1.card.tracking.k": "Monitorização",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografia",
  "f1.card.geo.v": "França · Bélgica francófona",
  "f1.card.confirm": "Confirmar hipótese  →",
  "f2.tag": "A MONITORIZAÇÃO",
  "f2.h": "Sinais de 100+ fontes UE verificadas. Monitorizado continuamente.",
  "f2.body": "O PulseLoop cobre as publicações, reguladores, organismos de financiamento e comunidades que moldam os mercados da UE em FR, DACH, Nórdicos e além. Inteligência que se acumula no seu workspace enquanto trabalha, para que esteja sempre informado antes de o seu cliente perguntar.",
  "f2.cta": "Entrar na lista →",
  "f2.feed.title": "Feed de sinais ao vivo",
  "f2.feed.new": "3 novos",
  "f2.feed.t1": "há 2 horas",
  "f2.feed.t2": "há 6 horas",
  "f2.feed.t3": "há 1 dia",
  "f2.feed.s1.text": "Typology lança gama profissional em exclusivo em farmácia...",
  "f2.feed.s1.chip": "Lançamento",
  "f2.feed.s2.text": "Novo regulamento de ingredientes cosméticos em vigor nos Estados-Membros...",
  "f2.feed.s2.chip": "Regulatório",
  "f2.feed.s3.text": "Novexpert capta 4M€ em Série A para acelerar retalho...",
  "f2.feed.s3.chip": "Investimento",
  "f3.tag": "A METODOLOGIA",
  "f3.h": "Saiba exatamente de onde vem a sua inteligência.",
  "f3.body": "Cada relatório é acompanhado por um Certificado de Metodologia que documenta as fontes utilizadas, os sinais recolhidos, os limites de confiança e o método de eliminação de duplicados. Transparência total em cada afirmação, para que possa apresentar com total confiança e defender cada insight na sala.",
  "f3.cta": "Entrar na lista →",
  "f3.cert.tag": "Certificado de Metodologia",
  "f3.cert.name1": "Skincare indie francês",
  "f3.cert.name2": "Briefing — T1 2026",
  "f3.cert.badge": "● Verificado",
  "f3.cert.l1.k": "Fontes",
  "f3.cert.l1.v": "23 fontes distintas",
  "f3.cert.l2.k": "Período",
  "f3.cert.l2.v": "01 mar – 31 mar 2026",
  "f3.cert.l3.k": "Sinais totais",
  "f3.cert.l3.v": "847 recolhidos",
  "f3.cert.l4.k": "Deduplicação",
  "f3.cert.l4.v": "Similaridade cosseno > 0,92",
  "f3.cert.l5.k": "Alta confiança",
  "f3.cert.l5.v": "10+ sinais, 5+ fontes",
  "f3.cert.l6.k": "Baixa confiança",
  "f3.cert.l6.v": "<3 sinais — sinalizado",
  "f3.cert.l7.k": "Idioma",
  "f3.cert.l7.v": "Francês",
  "f3.cert.foot": "Gerado por PulseLoop · pulseloop.io",
  "aud.h": "Para o consultor que fornece inteligência sobre a qual os clientes podem agir.",
  "aud.sub": "Quer trabalhe sozinho ou dirija uma agência, o PulseLoop oferece-lhe uma infraestrutura de inteligência verificada para que cada relatório que entregue seja um relatório sobre o qual os seus clientes possam agir com confiança.",
  "aud.solo.pill": "Mais popular",
  "aud.solo.name": "O Consultor Solo",
  "aud.solo.tag": "Você é a sua própria equipa. Precisa de soar como cinco.",
  "aud.solo.i1": "Relatórios com fontes em que os clientes confiam",
  "aud.solo.i2": "Respostas a pedido a partir de inteligência que já possui.",
  "aud.solo.i3": "Inteligência que se acumula a cada cliente",
  "aud.agency.pill": "Agências bem-vindas",
  "aud.agency.name": "A agência boutique",
  "aud.agency.tag": "Gere 6 contas. Cada uma merece a sua própria camada de inteligência de mercado.",
  "aud.agency.i1": "Workspace de inteligência separado por cliente",
  "aud.agency.i2": "Partilha de relatórios em white-label",
  "aud.agency.i3": "Uma assinatura, vários clientes cobertos",
  "aud.join": "Inscrever-me →",
  "final.cl1": "Uma sala de briefing",
  "final.cl2": "que nunca dorme.",
  "final.h": "Esteja entre os primeiros consultores.",
  "final.cta": "✛  Acesso antecipado",
  "final.success.h": "Você está na lista.",
  "final.success.s": "Acesso antecipado · Entraremos em contacto",
  "final.micro": "Acesso antecipado · Sediado na UE · Feito para a sua forma de trabalhar",
  "foot.tagline": "Inteligência de mercado UE verificada para consultores de crescimento e agências boutique.",
  "foot.product": "Produto",
  "foot.company": "Empresa",
  "foot.markets": "Mercados",
  "foot.about": "Sobre",
  "foot.contact": "Contacto",
  "foot.privacy": "Privacidade",
  "foot.france": "França",
  "foot.dach": "DACH",
  "foot.nordics": "Nórdicos",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · Hospedado na UE · Conforme RGPD",
  "foot.l.privacy": "Privacidade",
  "foot.l.terms": "Termos",
  "foot.l.status": "Estado",
  "toast.dup.title": "Já está na lista",
  "toast.dup.body": "O seu email já foi submetido, entraremos em contacto em breve.",
  "toast.ok.title": "Obrigado por se juntar!",
  "toast.ok.body": "Entraremos em contacto em breve.",
  "toast.err.title": "Não foi possível submeter",
  "toast.err.body": "Algo correu mal. Tente novamente.",
};

const es = {
  "nav.hypothesis": "INFORME DE INTELIGENCIA",
  "nav.intelligence": "MONITOREO",
  "nav.methodology": "Metodología",
  "nav.join": "Apuntarme",
  "nav.lang": "Idioma",
  "hero.intro": "Te presentamos PulseLoop",
  "hero.h1.norm": "La capa de inteligencia de mercado",
  "hero.h1.ital": "en la que puedes confiar y verificar.",
  "hero.sub": "PulseLoop monitoriza los mercados de la UE continuamente para que cada informe con el que entres en una sala esté verificado, citado y sea completamente tuyo para defender.",
  "hero.cta.primary": "Unirme a la lista",
  "hero.cta.access": "Obtener acceso",
  "hero.email.placeholder": "tu@email.com",
  "hero.success": "Estás en la lista — te contactaremos.",
  "hero.micro": "Acceso anticipado. Sin tarjeta. Con sede en la UE.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "En vivo",
  "hero.card.nav.brief": "Briefing de señales",
  "hero.card.nav.history": "Historial",
  "hero.card.nav.reports": "Informes",
  "hero.card.nav.cert": "Certificado",
  "hero.card.report.title": "SaaS RR.HH. francés — T2 2026",
  "hero.card.report.sub": "Briefing DACH · 847 señales · Generado el 14 abr 2026",
  "hero.card.chip.verified": "● Verificado",
  "hero.card.chip.high": "Alta confianza",
  "hero.card.chip.geo": "FR · DACH · Nórdicos",
  "hero.card.chip.sources": "23 fuentes",
  "hero.card.sig1.title": "Lucca recauda 15 M€ en Serie B para expandirse a DACH",
  "hero.card.sig1.body": "Competidor clave expandiéndose a tu mercado principal. Anuncio vía BFM Business.",
  "hero.card.sig2.title": "Nuevas obligaciones del AI Act de la UE para software de RR.HH. — marzo 2026",
  "hero.card.sig2.body": "Señal regulatoria que afecta a todos los proveedores SaaS RR.HH. en la UE. Fuente: Diario Oficial UE.",
  "hero.card.sig3.title": "Personio T1 2026: planificación de personal con IA",
  "hero.card.sig3.body": "Riesgo de paridad de funciones. Fuente: Personio Engineering Blog.",
  "trust.label": "COBERTURA DE INTELIGENCIA",
  "trust.f1": "Francia",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Países Nórdicos",
  "trust.f5": "Sur de Europa",
  "trust.f6": "CEE",
  "trust.f7": "Bálticos",
  "trust.f8": "Otros UE",
  "f1.tag": "El Informe de Inteligencia",
  "f1.h": "Inteligencia que comienza con tu brief, no con nuestras suposiciones.",
  "f1.body": "Pega el brief de tu cliente o responde algunas preguntas. PulseLoop estructura un plan de investigación en lenguaje claro y lo confirma contigo antes de recopilar una sola señal, para que lo que devuelva sea exactamente lo que tu cliente necesita.",
  "f1.cta": "Unirme a la lista →",
  "f1.card.title": "Hipótesis de investigación",
  "f1.card.live": "En vivo",
  "f1.card.market.k": "Mercado",
  "f1.card.market.v": "Skincare farmacia francés — marcas indie & retadoras",
  "f1.card.question.k": "Pregunta",
  "f1.card.question.v": "¿Cómo ganan las marcas indie espacio en estanterías y presencia social?",
  "f1.card.tracking.k": "Seguimiento",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografía",
  "f1.card.geo.v": "Francia · Bélgica francófona",
  "f1.card.confirm": "Confirmar hipótesis  →",
  "f2.tag": "EL MONITOREO",
  "f2.h": "Señales de más de 100 fuentes verificadas de la UE. Monitoreado continuamente.",
  "f2.body": "PulseLoop cubre las publicaciones, reguladores, organismos de financiación y comunidades que dan forma a los mercados de la UE en FR, DACH, países nórdicos y más allá. Inteligencia que se acumula en tu espacio de trabajo mientras trabajas, para que siempre estés informado antes de que tu cliente pregunte.",
  "f2.cta": "Unirme a la lista →",
  "f2.feed.title": "Feed de señales en vivo",
  "f2.feed.new": "3 nuevas",
  "f2.feed.t1": "hace 2 horas",
  "f2.feed.t2": "hace 6 horas",
  "f2.feed.t3": "hace 1 día",
  "f2.feed.s1.text": "Typology lanza gama profesional en exclusiva en farmacia...",
  "f2.feed.s1.chip": "Lanzamiento",
  "f2.feed.s2.text": "Nueva normativa de ingredientes cosméticos entra en vigor en los Estados Miembros...",
  "f2.feed.s2.chip": "Regulatorio",
  "f2.feed.s3.text": "Novexpert recauda 4M€ en Serie A para acelerar retail...",
  "f2.feed.s3.chip": "Financiación",
  "f3.tag": "LA METODOLOGÍA",
  "f3.h": "Sepa exactamente de dónde proviene su inteligencia.",
  "f3.body": "Cada informe incluye un Certificado de Metodología que documenta las fuentes utilizadas, las señales recopiladas, los umbrales de confianza y el método de deduplicación. Transparencia total en cada afirmación, para que pueda presentar con total confianza y defender cada idea en la sala.",
  "f3.cta": "Unirme a la lista →",
  "f3.cert.tag": "Certificado de Metodología",
  "f3.cert.name1": "Skincare indie francés",
  "f3.cert.name2": "Briefing — T1 2026",
  "f3.cert.badge": "● Verificado",
  "f3.cert.l1.k": "Fuentes",
  "f3.cert.l1.v": "23 fuentes distintas",
  "f3.cert.l2.k": "Periodo",
  "f3.cert.l2.v": "01 mar – 31 mar 2026",
  "f3.cert.l3.k": "Señales totales",
  "f3.cert.l3.v": "847 recopiladas",
  "f3.cert.l4.k": "Deduplicación",
  "f3.cert.l4.v": "Similitud coseno > 0,92",
  "f3.cert.l5.k": "Alta confianza",
  "f3.cert.l5.v": "10+ señales, 5+ fuentes",
  "f3.cert.l6.k": "Baja confianza",
  "f3.cert.l6.v": "<3 señales — marcado",
  "f3.cert.l7.k": "Idioma",
  "f3.cert.l7.v": "Francés",
  "f3.cert.foot": "Generado por PulseLoop · pulseloop.io",
  "aud.h": "Para el consultor que entrega inteligencia sobre la cual los clientes pueden actuar.",
  "aud.sub": "Ya sea que trabajes solo o dirijas una agencia, PulseLoop te brinda una infraestructura de inteligencia verificada para que cada informe que entregues sea uno sobre el cual tus clientes puedan actuar con confianza.",
  "aud.solo.pill": "Más popular",
  "aud.solo.name": "El consultor solo",
  "aud.solo.tag": "Eres tu propio equipo de investigación. Tienes que sonar como cinco.",
  "aud.solo.i1": "Informes con fuentes en los que tus clientes confían",
  "aud.solo.i2": "Respuestas bajo demanda a partir de la inteligencia que ya posees.",
  "aud.solo.i3": "Inteligencia que se acumula con cada cliente",
  "aud.agency.pill": "Agencias bienvenidas",
  "aud.agency.name": "La agencia boutique",
  "aud.agency.tag": "Gestionas 6 cuentas. Cada una merece su propia capa de inteligencia de mercado.",
  "aud.agency.i1": "Espacio de inteligencia separado por cliente",
  "aud.agency.i2": "Compartir informes en marca blanca",
  "aud.agency.i3": "Una suscripción, varios clientes cubiertos",
  "aud.join": "Apuntarme →",
  "final.cl1": "Una sala de briefing",
  "final.cl2": "que nunca duerme.",
  "final.h": "Sé de los primeros consultores en entrar.",
  "final.cta": "✛  Acceso anticipado",
  "final.success.h": "Estás en la lista.",
  "final.success.s": "Acceso anticipado · Te contactaremos",
  "final.micro": "Acceso anticipado · Con sede en la UE · Hecho para tu forma de trabajar",
  "foot.tagline": "Inteligencia de mercado UE verificada para consultores de crecimiento y agencias boutique.",
  "foot.product": "Producto",
  "foot.company": "Empresa",
  "foot.markets": "Mercados",
  "foot.about": "Acerca",
  "foot.contact": "Contacto",
  "foot.privacy": "Privacidad",
  "foot.france": "Francia",
  "foot.dach": "DACH",
  "foot.nordics": "Nórdicos",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · Alojado en la UE · Conforme RGPD",
  "foot.l.privacy": "Privacidad",
  "foot.l.terms": "Términos",
  "foot.l.status": "Estado",
  "toast.dup.title": "Ya estás en la lista",
  "toast.dup.body": "Tu email ya fue enviado, te contactaremos pronto.",
  "toast.ok.title": "¡Gracias por unirte!",
  "toast.ok.body": "Te contactaremos pronto.",
  "toast.err.title": "No se pudo enviar",
  "toast.err.body": "Algo salió mal. Inténtalo de nuevo.",
};

const sv = {
  "nav.hypothesis": "INTELLIGENCE BRIEF",
  "nav.intelligence": "MONITORERING",
  "nav.methodology": "Metodik",
  "nav.join": "Gå med",
  "nav.lang": "Språk",
  "hero.intro": "Vi presenterar PulseLoop",
  "hero.h1.norm": "Marknadsinformationslagret",
  "hero.h1.ital": "som du kan lita på och verifiera.",
  "hero.sub": "PulseLoop övervakar EU-marknaderna kontinuerligt så att varje rapport du går in i ett rum med är verifierad, källhänvisad och helt din att försvara.",
  "hero.cta.primary": "Gå med i väntelistan",
  "hero.cta.access": "Få tillgång",
  "hero.email.placeholder": "din@email.com",
  "hero.success": "Du är på listan — vi hör av oss.",
  "hero.micro": "Tidig tillgång. Inget kreditkort. EU-baserad.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Live",
  "hero.card.nav.brief": "Signal-briefing",
  "hero.card.nav.history": "Historik",
  "hero.card.nav.reports": "Rapporter",
  "hero.card.nav.cert": "Certifikat",
  "hero.card.report.title": "Fransk SaaS HR-Tech — Q2 2026",
  "hero.card.report.sub": "DACH-marknadsbriefing · 847 signaler · Genererad 14 apr 2026",
  "hero.card.chip.verified": "● Verifierad",
  "hero.card.chip.high": "Hög tillförlitlighet",
  "hero.card.chip.geo": "FR · DACH · Norden",
  "hero.card.chip.sources": "23 källor",
  "hero.card.sig1.title": "Lucca tar in 15M€ i Series B för DACH-expansion",
  "hero.card.sig1.body": "Nyckelkonkurrent expanderar in på din primärmarknad. Annonserat via BFM Business.",
  "hero.card.sig2.title": "Nya EU AI Act-krav för HR-mjukvara — mars 2026",
  "hero.card.sig2.body": "Regulatorisk signal som påverkar alla SaaS HR-leverantörer i EU. Källa: EU:s officiella tidning.",
  "hero.card.sig3.title": "Personio Q1 2026: AI-driven personalplanering",
  "hero.card.sig3.body": "Risk för funktionsparitet. Källa: Personio Engineering Blog.",
  "trust.label": "UNDERRÄTTELSETÄCKNING",
  "trust.f1": "Frankrike",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Norden",
  "trust.f5": "Södra EU",
  "trust.f6": "CEE",
  "trust.f7": "Baltikum",
  "trust.f8": "Övriga EU",
  "f1.tag": "Intelligence-briefen",
  "f1.h": "Intelligens som börjar med din brief, inte våra antaganden.",
  "f1.body": "Klistra in kundens brief eller svara på några frågor. PulseLoop strukturerar en researchplan på tydligt språk och bekräftar den med dig innan en enda signal samlas in, så att det som kommer tillbaka är exakt vad din kund behöver.",
  "f1.cta": "Gå med i väntelistan →",
  "f1.card.title": "Researchhypotes",
  "f1.card.live": "Live",
  "f1.card.market.k": "Marknad",
  "f1.card.market.v": "Fransk apotekshudvård — indie- & utmanarvarumärken",
  "f1.card.question.k": "Fråga",
  "f1.card.question.v": "Hur vinner indie-varumärken hyllplats och social närvaro?",
  "f1.card.tracking.k": "Bevakning",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografi",
  "f1.card.geo.v": "Frankrike · fransktalande Belgien",
  "f1.card.confirm": "Bekräfta hypotes  →",
  "f2.tag": "BEVAKNINGEN",
  "f2.h": "Signaler från 100+ verifierade EU-källor. Kontinuerligt övervakad.",
  "f2.body": "PulseLoop täcker publikationer, regulatorer, finansiärer och communities som formar EU-marknader i FR, DACH, Norden och bortom. Intelligens som ackumuleras i din workspace medan du arbetar, så att du alltid är briefad innan din kund frågar.",
  "f2.cta": "Gå med i väntelistan →",
  "f2.feed.title": "Live signalflöde",
  "f2.feed.new": "3 nya",
  "f2.feed.t1": "för 2 timmar sedan",
  "f2.feed.t2": "för 6 timmar sedan",
  "f2.feed.t3": "för 1 dag sedan",
  "f2.feed.s1.text": "Typology lanserar professionell serie exklusivt på apotek...",
  "f2.feed.s1.chip": "Produktlansering",
  "f2.feed.s2.text": "Ny förordning om kosmetiska ingredienser börjar gälla i medlemsländerna...",
  "f2.feed.s2.chip": "Regulatoriskt",
  "f2.feed.s3.text": "Novexpert tar in 4M€ i Series A för retail-utbyggnad...",
  "f2.feed.s3.chip": "Finansiering",
  "f3.tag": "METODIKEN",
  "f3.h": "Vet exakt var din intelligens kommer ifrån.",
  "f3.body": "Varje rapport levereras med ett metodikcertifikat som dokumenterar använda källor, insamlade signaler, konfidensnivåer och dedupliceringsmetod. Full transparens för varje påstående, så att du kan presentera med fullt förtroende och försvara varje insikt i rummet.",
  "f3.cta": "Gå med i väntelistan →",
  "f3.cert.tag": "Metodikcertifikat",
  "f3.cert.name1": "Fransk indie-hudvård",
  "f3.cert.name2": "Marknadsbriefing — Q1 2026",
  "f3.cert.badge": "● Verifierad",
  "f3.cert.l1.k": "Källor",
  "f3.cert.l1.v": "23 distinkta källor",
  "f3.cert.l2.k": "Tidsperiod",
  "f3.cert.l2.v": "01 mar – 31 mar 2026",
  "f3.cert.l3.k": "Totala signaler",
  "f3.cert.l3.v": "847 hämtade",
  "f3.cert.l4.k": "Deduplicering",
  "f3.cert.l4.v": "Cosinuslikhet > 0,92",
  "f3.cert.l5.k": "Hög tillförlitlighet",
  "f3.cert.l5.v": "10+ signaler, 5+ källor",
  "f3.cert.l6.k": "Låg tillförlitlighet",
  "f3.cert.l6.v": "<3 signaler — flaggad",
  "f3.cert.l7.k": "Språk",
  "f3.cert.l7.v": "Franska",
  "f3.cert.foot": "Genererad av PulseLoop · pulseloop.io",
  "aud.h": "För konsulten som levererar intelligens som klienter kan agera på.",
  "aud.sub": "Oavsett om du arbetar ensam eller driver en byrå, ger PulseLoop dig en verifierad underrättelseinfrastruktur så att varje rapport du levererar är en som dina kunder kan agera på med förtroende.",
  "aud.solo.pill": "Mest populär",
  "aud.solo.name": "Solokonsulten",
  "aud.solo.tag": "Du är ditt eget research-team. Du måste låta som fem.",
  "aud.solo.i1": "Källhänvisade rapporter dina kunder litar på",
  "aud.solo.i2": "Svar på begäran från intelligens som du redan äger.",
  "aud.solo.i3": "Intelligens som ackumuleras med varje kund",
  "aud.agency.pill": "Byråer välkomna",
  "aud.agency.name": "Boutique-byrån",
  "aud.agency.tag": "Du hanterar 6 kundkonton. Var och en förtjänar sitt eget marknadsinformationslager.",
  "aud.agency.i1": "Separat intelligens-arbetsyta per kund",
  "aud.agency.i2": "White-label rapportdelning",
  "aud.agency.i3": "En prenumeration, flera kunder täckta",
  "aud.join": "Gå med →",
  "final.cl1": "Ett briefingrum",
  "final.cl2": "som aldrig sover.",
  "final.h": "Bli en av de första konsulterna in.",
  "final.cta": "✛  Få tidig tillgång",
  "final.success.h": "Du är på listan.",
  "final.success.s": "Tidig tillgång · Vi hör av oss",
  "final.micro": "Tidig tillgång · EU-baserad · Byggd för ditt arbetssätt",
  "foot.tagline": "Verifierad EU-marknadsintelligens för tillväxtkonsulter och boutique-byråer.",
  "foot.product": "Produkt",
  "foot.company": "Företag",
  "foot.markets": "Marknader",
  "foot.about": "Om oss",
  "foot.contact": "Kontakt",
  "foot.privacy": "Integritet",
  "foot.france": "Frankrike",
  "foot.dach": "DACH",
  "foot.nordics": "Norden",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · EU-värd · GDPR-kompatibel",
  "foot.l.privacy": "Integritet",
  "foot.l.terms": "Villkor",
  "foot.l.status": "Status",
  "toast.dup.title": "Redan på listan",
  "toast.dup.body": "Din e-post har redan skickats in, vi hör av oss snart.",
  "toast.ok.title": "Tack för att du gick med!",
  "toast.ok.body": "Vi hör av oss snart.",
  "toast.err.title": "Kunde inte skicka",
  "toast.err.body": "Något gick fel. Försök igen.",
};

const ro = {
  "nav.hypothesis": "BRIEF DE INTELIGENȚĂ",
  "nav.intelligence": "MONITORIZARE",
  "nav.methodology": "Metodologie",
  "nav.join": "Înscrie-te",
  "nav.lang": "Limbă",
  "hero.intro": "Vă prezentăm PulseLoop",
  "hero.h1.norm": "Stratul de inteligență a pieței",
  "hero.h1.ital": "în care puteți avea încredere și pe care o puteți verifica.",
  "hero.sub": "PulseLoop monitorizează piețele UE în mod continuu, astfel încât fiecare raport cu care intrați într-o încăpere să fie verificat, citat și în întregime al vostru pentru a-l susține.",
  "hero.cta.primary": "Înscrie-te pe listă",
  "hero.cta.access": "Obține acces",
  "hero.email.placeholder": "email@exemplu.com",
  "hero.success": "Ești pe listă — te vom contacta.",
  "hero.micro": "Acces anticipat. Fără card. Sediu în UE.",
  "hero.card.brand": "PulseLoop Intelligence",
  "hero.card.live": "Live",
  "hero.card.nav.brief": "Briefing semnale",
  "hero.card.nav.history": "Istoric",
  "hero.card.nav.reports": "Rapoarte",
  "hero.card.nav.cert": "Certificat",
  "hero.card.report.title": "SaaS HR francez — T2 2026",
  "hero.card.report.sub": "Briefing DACH · 847 semnale · Generat la 14 apr. 2026",
  "hero.card.chip.verified": "● Verificat",
  "hero.card.chip.high": "Încredere ridicată",
  "hero.card.chip.geo": "FR · DACH · Nordice",
  "hero.card.chip.sources": "23 surse",
  "hero.card.sig1.title": "Lucca strânge 15 mil. € în Series B pentru extindere în DACH",
  "hero.card.sig1.body": "Concurent-cheie se extinde pe piața voastră principală. Anunț via BFM Business.",
  "hero.card.sig2.title": "Noi obligații EU AI Act pentru software-ul HR — martie 2026",
  "hero.card.sig2.body": "Semnal regulator care afectează toți furnizorii SaaS HR din UE. Sursă: Jurnalul Oficial UE.",
  "hero.card.sig3.title": "Personio T1 2026: planificare HR cu IA",
  "hero.card.sig3.body": "Risc de paritate funcțională. Sursă: Personio Engineering Blog.",
  "trust.label": "ACOPERIREA INFORMAȚIILOR",
  "trust.f1": "Franța",
  "trust.f2": "DACH",
  "trust.f3": "Benelux",
  "trust.f4": "Nordice",
  "trust.f5": "Europa de Sud",
  "trust.f6": "CEE",
  "trust.f7": "Statele Baltice",
  "trust.f8": "Alte UE",
  "f1.tag": "Briefingul de Intelligence",
  "f1.h": "Intelligence care începe cu briefingul tău, nu cu presupunerile noastre.",
  "f1.body": "Lipește briefingul clientului tău sau răspunde la câteva întrebări. PulseLoop structurează un plan de cercetare în limbaj simplu și îl confirmă cu tine înainte de a colecta un singur semnal, astfel încât ceea ce primești să fie exact ceea ce are nevoie clientul tău.",
  "f1.cta": "Înscrie-te pe listă →",
  "f1.card.title": "Ipoteză de cercetare",
  "f1.card.live": "Live",
  "f1.card.market.k": "Piață",
  "f1.card.market.v": "Skincare francez de farmacie — branduri indie & challenger",
  "f1.card.question.k": "Întrebare",
  "f1.card.question.v": "Cum câștigă brandurile indie spațiu pe raft și prezență socială?",
  "f1.card.tracking.k": "Urmărire",
  "f1.card.tracking.v": "Typology · Novexpert · La Roche-Posay · Caudalie",
  "f1.card.geo.k": "Geografie",
  "f1.card.geo.v": "Franța · Belgia francofonă",
  "f1.card.confirm": "Confirmă ipoteza  →",
  "f2.tag": "MONITORIZAREA",
  "f2.h": "Semnale din peste 100 de surse UE verificate. Monitorizat continuu.",
  "f2.body": "PulseLoop acoperă publicațiile, autoritățile de reglementare, organismele de finanțare și comunitățile care modelează piețele UE în FR, DACH, Nordics și nu numai. Intelligence care se acumulează în spațiul tău de lucru în timp ce lucrezi, astfel încât să fii mereu informat înainte ca clientul tău să întrebe.",
  "f2.cta": "Înscrie-te pe listă →",
  "f2.feed.title": "Flux semnale live",
  "f2.feed.new": "3 noi",
  "f2.feed.t1": "acum 2 ore",
  "f2.feed.t2": "acum 6 ore",
  "f2.feed.t3": "acum 1 zi",
  "f2.feed.s1.text": "Typology lansează o gamă profesională în exclusivitate în farmacii...",
  "f2.feed.s1.chip": "Lansare produs",
  "f2.feed.s2.text": "O nouă reglementare privind ingredientele cosmetice intră în vigoare în statele membre...",
  "f2.feed.s2.chip": "Reglementare",
  "f2.feed.s3.text": "Novexpert strânge 4 mil. € în Series A pentru a accelera retail-ul...",
  "f2.feed.s3.chip": "Finanțare",
  "f3.tag": "METODOLOGIA",
  "f3.h": "Știi exact de unde provin informațiile tale.",
  "f3.body": "Fiecare raport este însoțit de un certificat de metodologie care documentează sursele utilizate, semnalele colectate, pragurile de încredere și metoda de deduplicare. Transparență totală pentru fiecare afirmație, astfel încât să poți prezenta cu încredere deplină și să susții fiecare informație în încăpere.",
  "f3.cta": "Înscrie-te pe listă →",
  "f3.cert.tag": "Certificat de metodologie",
  "f3.cert.name1": "Skincare indie francez",
  "f3.cert.name2": "Briefing — T1 2026",
  "f3.cert.badge": "● Verificat",
  "f3.cert.l1.k": "Surse",
  "f3.cert.l1.v": "23 surse distincte",
  "f3.cert.l2.k": "Perioadă",
  "f3.cert.l2.v": "01 mar – 31 mar 2026",
  "f3.cert.l3.k": "Semnale totale",
  "f3.cert.l3.v": "847 colectate",
  "f3.cert.l4.k": "Deduplicare",
  "f3.cert.l4.v": "Similaritate cosinus > 0,92",
  "f3.cert.l5.k": "Încredere ridicată",
  "f3.cert.l5.v": "10+ semnale, 5+ surse",
  "f3.cert.l6.k": "Încredere scăzută",
  "f3.cert.l6.v": "<3 semnale — marcate",
  "f3.cert.l7.k": "Limbă",
  "f3.cert.l7.v": "Franceză",
  "f3.cert.foot": "Generat de PulseLoop · pulseloop.io",
  "aud.h": "Pentru consultantul care oferă informații pe baza cărora clienții pot acționa.",
  "aud.sub": "Indiferent dacă lucrezi singur sau conduci o agenție, PulseLoop îți oferă o infrastructură de informații verificată, astfel încât fiecare raport pe care îl livrezi să fie unul pe baza căruia clienții tăi să poată acționa cu încredere.",
  "aud.solo.pill": "Cel mai popular",
  "aud.solo.name": "Consultantul Solo",
  "aud.solo.tag": "Ești propria ta echipă de cercetare. Trebuie să sune ca și cum ai fi cinci.",
  "aud.solo.i1": "Rapoarte cu surse în care clienții au încredere",
  "aud.solo.i2": "Răspunsuri la cerere din informațiile pe care le deții deja.",
  "aud.solo.i3": "Inteligență care se acumulează cu fiecare client",
  "aud.agency.pill": "Agenții bine-venite",
  "aud.agency.name": "Agenția Boutique",
  "aud.agency.tag": "Gestionezi 6 conturi de clienți. Fiecare merită propriul strat de inteligență a pieței.",
  "aud.agency.i1": "Spațiu de intelligence separat per client",
  "aud.agency.i2": "Rapoarte white-label",
  "aud.agency.i3": "Un abonament, mai mulți clienți acoperiți",
  "aud.join": "Înscrie-te →",
  "final.cl1": "O sală de briefing",
  "final.cl2": "care nu doarme niciodată.",
  "final.h": "Fii printre primii consultanți admiși.",
  "final.cta": "✛  Acces anticipat",
  "final.success.h": "Ești pe listă.",
  "final.success.s": "Acces anticipat · Te vom contacta",
  "final.micro": "Acces anticipat · Sediu în UE · Construit pentru modul tău de lucru",
  "foot.tagline": "Informații de piață UE verificate pentru consultanți de creștere și agenții boutique.",
  "foot.product": "Produs",
  "foot.company": "Companie",
  "foot.markets": "Piețe",
  "foot.about": "Despre",
  "foot.contact": "Contact",
  "foot.privacy": "Confidențialitate",
  "foot.france": "Franța",
  "foot.dach": "DACH",
  "foot.nordics": "Nordice",
  "foot.benelux": "Benelux",
  "foot.copy": "© 2026 PulseLoop · Găzduit în UE · Conform GDPR",
  "foot.l.privacy": "Confidențialitate",
  "foot.l.terms": "Termeni",
  "foot.l.status": "Stare",
  "toast.dup.title": "Deja pe listă",
  "toast.dup.body": "Adresa ta de email a fost deja trimisă, te vom contacta în curând.",
  "toast.ok.title": "Mulțumim că te-ai înscris!",
  "toast.ok.body": "Te vom contacta în curând.",
  "toast.err.title": "Trimiterea a eșuat",
  "toast.err.body": "Ceva n-a mers. Te rog încearcă din nou.",
};

const TRANSLATIONS = { en, fr, de, nl, pt, es, sv, ro };

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && TRANSLATIONS[saved] ? saved : "en";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const value = useMemo(() => {
    const dict = TRANSLATIONS[lang] || en;
    const t = (key) => (key in dict ? dict[key] : (en[key] ?? key));
    return { lang, setLang, t };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside <I18nProvider>");
  return ctx;
}

// Fallback for non-component callers (e.g., toasts inside async helpers).
let activeLang = "en";
export function setActiveLang(l) {
  activeLang = TRANSLATIONS[l] ? l : "en";
}
export function tStatic(key) {
  const dict = TRANSLATIONS[activeLang] || en;
  return key in dict ? dict[key] : (en[key] ?? key);
}
